import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Box,
  Checkbox,
  FormControlLabel,
  Divider,
  Typography,
  Button,
  FormControl,
  Select,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  SortByAlpha as SortAscIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

const ColumnFilterMenu = ({ column, data }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterMode, setFilterMode] = useState('value'); // 'value' or 'condition'
  const [conditionType, setConditionType] = useState('contains');
  const [conditionValue, setConditionValue] = useState('');

  const uniqueValues = useMemo(() => {
    const values = data.map(row => {
      const value = column.accessorFn ? column.accessorFn(row) : row[column.accessorKey];
      return value !== null && value !== undefined ? String(value) : '';
    });
    return [...new Set(values)].sort();
  }, [data, column]);

  const currentFilter = column.getFilterValue() || {};
  const selectedValues = currentFilter.values || uniqueValues; // Default to all values selected

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSort = (direction) => {
    column.toggleSorting(direction === 'desc');
    handleMenuClose();
  };

  const handleValueFilterChange = (value, checked) => {
    let newSelectedValues;
    if (checked) {
      newSelectedValues = [...selectedValues, value];
    } else {
      newSelectedValues = selectedValues.filter(v => v !== value);
    }
    
    // If all values are selected, clear the filter (show all)
    // If some values are deselected, apply the filter
    const filterValue = newSelectedValues.length === uniqueValues.length 
      ? undefined 
      : {
          type: 'value',
          values: newSelectedValues
        };
    
    column.setFilterValue(filterValue);
  };

  const handleConditionFilter = () => {
    column.setFilterValue({
      type: 'condition',
      condition: conditionType,
      value: conditionValue
    });
    handleMenuClose();
  };

  const handleClearFilter = () => {
    column.setFilterValue(undefined);
    setConditionValue('');
    handleMenuClose();
  };

  const applyValueFilter = () => {
    // Filter is already applied through handleValueFilterChange
    handleMenuClose();
  };

  return (
    <>
    <IconButton
        size="small"
        onClick={handleMenuOpen}
        sx={{ 
          ml: 1, 
          opacity: column.getFilterValue() ? 1 : 0.8,
      backgroundColor: column.getFilterValue() ? 'action.selected' : 'rgba(0,0,0,0)',
          '&:hover': {
            backgroundColor: 'action.hover',
          }
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { 
            width: 320, 
            maxHeight: 400,
            backgroundColor: '#1a1a1a',
            '& .MuiMenuItem-root': {
              color: '#ffffff',
              '&:hover': {
                backgroundColor: 'rgba(101, 115, 195, 0.1)',
              }
            }
          }
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        {/* Sort Options */}
        <MenuItem onClick={() => handleSort('asc')}>
          <SortAscIcon sx={{ mr: 1 }} />
          Sort A → Z
        </MenuItem>
        <MenuItem onClick={() => handleSort('desc')}>
          <SortAscIcon sx={{ mr: 1, transform: 'rotate(180deg)' }} />
          Sort Z → A
        </MenuItem>
        
        <Divider />
        
        {/* Filter Mode Toggle */}
        <Box sx={{ p: 2 }}>
          <FormControl fullWidth size="small" sx={{ 
            mb: 2,
            '& .MuiInputLabel-root': {
              color: '#6573c3',
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#6573c3',
              },
              '&:hover fieldset': {
                borderColor: '#3f51b5',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6573c3',
              },
            },
            '& .MuiSelect-select': {
              color: '#ffffff',
            }
          }}>
            <InputLabel>Filter Type</InputLabel>
            <Select
              value={filterMode}
              label="Filter Type"
              onChange={(e) => setFilterMode(e.target.value)}
            >
              <MenuItem value="value">Filter by Values</MenuItem>
              <MenuItem value="condition">Filter by Condition</MenuItem>
            </Select>
          </FormControl>

          {filterMode === 'value' ? (
            /* Value Filter */
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  Select values to show:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    size="small" 
                    variant="text"
                    sx={{ color: '#6573c3', '&:hover': { backgroundColor: 'rgba(101, 115, 195, 0.1)' } }}
                    onClick={() => {
                      column.setFilterValue(undefined);
                    }}
                  >
                    All
                  </Button>
                  <Button 
                    size="small" 
                    variant="text"
                    sx={{ color: '#6573c3', '&:hover': { backgroundColor: 'rgba(101, 115, 195, 0.1)' } }}
                    onClick={() => {
                      column.setFilterValue({
                        type: 'value',
                        values: []
                      });
                    }}
                  >
                    None
                  </Button>
                </Box>
              </Box>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {uniqueValues.map((value) => (
                  <FormControlLabel
                    key={value}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedValues.includes(value)}
                        onChange={(e) => handleValueFilterChange(value, e.target.checked)}
                        sx={{ 
                          color: '#6573c3',
                          '&.Mui-checked': {
                            color: '#6573c3',
                          }
                        }}
                      />
                    }
                    label={value || '(blank)'}
                    sx={{ 
                      display: 'block',
                      '& .MuiFormControlLabel-label': {
                        color: '#ffffff'
                      }
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  variant="contained"
                  sx={{ 
                    backgroundColor: '#6573c3', 
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#3f51b5' }
                  }}
                  onClick={applyValueFilter}
                >
                  Apply
                </Button>
                <Button 
                  size="small" 
                  variant="outlined"
                  sx={{ 
                    borderColor: '#6573c3', 
                    color: '#6573c3',
                    '&:hover': { 
                      borderColor: '#3f51b5',
                      backgroundColor: 'rgba(101, 115, 195, 0.1)'
                    }
                  }}
                  onClick={handleClearFilter}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          ) : (
            /* Condition Filter */
            <Box>
              <FormControl fullWidth size="small" sx={{ 
                mb: 2,
                '& .MuiInputLabel-root': {
                  color: '#6573c3',
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#6573c3',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3f51b5',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6573c3',
                  },
                },
                '& .MuiSelect-select': {
                  color: '#ffffff',
                }
              }}>
                <InputLabel>Condition</InputLabel>
                <Select
                  value={conditionType}
                  label="Condition"
                  onChange={(e) => setConditionType(e.target.value)}
                >
                  <MenuItem value="contains">Contains</MenuItem>
                  <MenuItem value="equals">Equals</MenuItem>
                  <MenuItem value="startsWith">Starts with</MenuItem>
                  <MenuItem value="endsWith">Ends with</MenuItem>
                  <MenuItem value="greaterThan">Greater than</MenuItem>
                  <MenuItem value="lessThan">Less than</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label="Value"
                value={conditionValue}
                onChange={(e) => setConditionValue(e.target.value)}
                sx={{ 
                  mb: 2,
                  '& .MuiInputLabel-root': {
                    color: '#6573c3',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#6573c3',
                    },
                    '&:hover fieldset': {
                      borderColor: '#3f51b5',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6573c3',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: '#ffffff',
                  }
                }}
                autoComplete="off"
                inputProps={{
                  autoComplete: 'off',
                  autoCorrect: 'off',
                  autoCapitalize: 'off',
                  spellCheck: false
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  variant="contained"
                  sx={{ 
                    backgroundColor: '#6573c3', 
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#3f51b5' }
                  }}
                  onClick={handleConditionFilter}
                >
                  Apply
                </Button>
                <Button 
                  size="small" 
                  variant="outlined"
                  sx={{ 
                    borderColor: '#6573c3', 
                    color: '#6573c3',
                    '&:hover': { 
                      borderColor: '#3f51b5',
                      backgroundColor: 'rgba(101, 115, 195, 0.1)'
                    }
                  }}
                  onClick={handleClearFilter}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};

const PaginationControls = ({ pagination, onPaginationChange }) => {
  const [pageInput, setPageInput] = useState(pagination.page.toString());

  React.useEffect(() => {
    setPageInput(pagination.page.toString());
  }, [pagination.page]);

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = () => {
    const newPage = parseInt(pageInput, 10);
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      onPaginationChange && onPaginationChange(newPage, pagination.limit);
    } else {
      // Reset to current page if invalid
      setPageInput(pagination.page.toString());
    }
  };

  const handlePageInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageInputSubmit();
    }
  };

  return (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Rows per page selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pagination.limit}
              onChange={(e) => onPaginationChange && onPaginationChange(1, parseInt(e.target.value, 10))}
              variant="outlined"
              sx={{ 
                color: 'text.primary',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6573c3',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Page navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button 
            variant="outlined" 
            size="small"
            disabled={pagination.page <= 1}
            onClick={() => {
              const currentPage = parseInt(pagination.page, 10) || 1;
              const prevPage = Math.max(currentPage - 1, 1);
              onPaginationChange && onPaginationChange(prevPage, parseInt(pagination.limit, 10) || 10);
            }}
            sx={{ 
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            Previous
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Page
            </Typography>
            <TextField
              value={pageInput}
              onChange={handlePageInputChange}
              onBlur={handlePageInputSubmit}
              onKeyPress={handlePageInputKeyPress}
              size="small"
              variant="outlined"
              sx={{ 
                width: 60,
                '& .MuiOutlinedInput-input': {
                  textAlign: 'center',
                  padding: '4px 8px',
                  color: 'text.primary',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                },
                '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6573c3',
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              of {pagination.totalPages}
            </Typography>
          </Box>
          
          <Button 
            variant="outlined" 
            size="small"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => {
              const currentPage = parseInt(pagination.page, 10) || 1;
              const nextPage = currentPage + 1;
              console.log('Next button clicked. Current page:', currentPage, 'Going to:', nextPage);
              onPaginationChange && onPaginationChange(nextPage, parseInt(pagination.limit, 10) || 10);
            }}
            sx={{ 
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              '&.Mui-disabled': {
                borderColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              }
            }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const TanStackTable = ({ 
  data, 
  columns, 
  onRowClick, 
  getRowClassName,
  enableRowSelection = false,
  enablePagination = false,
  pagination,
  onPaginationChange,
  searchQuery = '',
  onSearchChange,
  sortBy,
  sortOrder,
  onSortChange
}) => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery || '');

  // Sync local search query with prop changes
  React.useEffect(() => {
    setLocalSearchQuery(searchQuery || '');
  }, [searchQuery]);


  const enhancedColumns = useMemo(() => {
    return columns.map(col => ({
      ...col,
      filterFn: 'custom',
      header: ({ column }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 'bold',
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' }
            }}
            onClick={() => {
              if (enablePagination && onSortChange) {
                // Use server-side sorting
                let newSortOrder;
                if (sortBy === col.accessorKey) {
                  // Toggle the current sort order
                  newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
                } else {
                  // New column, start with DESC
                  newSortOrder = 'DESC';
                }
                onSortChange(col.accessorKey, newSortOrder);
              } else {
                // Use client-side sorting
                column.toggleSorting();
              }
            }}
          >
            {col.header}
            {enablePagination ? (
              // Server-side sorting indicators
              sortBy === col.accessorKey && sortOrder === 'ASC' && ' ↑'
            ) || (
              sortBy === col.accessorKey && sortOrder === 'DESC' && ' ↓'
            ) : (
              // Client-side sorting indicators
              <>
                {column.getIsSorted() === 'asc' && ' ↑'}
                {column.getIsSorted() === 'desc' && ' ↓'}
              </>
            )}
          </Typography>
          {!enablePagination && <ColumnFilterMenu column={column} data={data} />}
        </Box>
      ),
    }));
  }, [columns, data, enablePagination, sortBy, sortOrder, onSortChange]);

  // Create table with enhanced columns
  const tableConfig = {
    data,
    columns: enhancedColumns,
    getCoreRowModel: getCoreRowModel(),
  };

  // Only add client-side features when pagination is disabled
  if (!enablePagination) {
    tableConfig.state = { globalFilter };
    tableConfig.onGlobalFilterChange = setGlobalFilter;
    tableConfig.getFilteredRowModel = getFilteredRowModel();
    tableConfig.getSortedRowModel = getSortedRowModel();
  }

  const tableWithEnhancedColumns = useReactTable({
    ...tableConfig,
    filterFns: {
      custom: (row, columnId, filterValue) => {
        // If no filter is applied, show all rows
        if (!filterValue) return true;
        
        const cellValue = row.getValue(columnId);
        const stringValue = cellValue !== null && cellValue !== undefined ? String(cellValue) : '';
        
        if (filterValue.type === 'value') {
          // If no values are specified or values array is empty, show all
          if (!filterValue.values || filterValue.values.length === 0) return false;
          return filterValue.values.includes(stringValue);
        } else if (filterValue.type === 'condition') {
          const { condition, value } = filterValue;
          if (!value) return true;
          
          const lowerCellValue = stringValue.toLowerCase();
          const lowerFilterValue = value.toLowerCase();
          
          switch (condition) {
            case 'contains':
              return lowerCellValue.includes(lowerFilterValue);
            case 'equals':
              return lowerCellValue === lowerFilterValue;
            case 'startsWith':
              return lowerCellValue.startsWith(lowerFilterValue);
            case 'endsWith':
              return lowerCellValue.endsWith(lowerFilterValue);
            case 'greaterThan':
              return parseFloat(cellValue) > parseFloat(value);
            case 'lessThan':
              return parseFloat(cellValue) < parseFloat(value);
            default:
              return true;
          }
        }
        return true;
      }
    },
    globalFilterFn: 'includesString',
  });

  // Prevent ResizeObserver errors in development
  React.useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('error', handleResizeObserverError);
    return () => window.removeEventListener('error', handleResizeObserverError);
  }, []);

  // Ensure we have a sanitizer to avoid passing 'transparent' to MUI color utils
  const __sanitizeColor = (val) => (val === 'transparent' ? 'rgba(0,0,0,0)' : val);
  const __sanitizeRowSx = (sx) => {
    if (!sx || typeof sx !== 'object') return {};
    const out = { ...sx };
    if (out.backgroundColor) out.backgroundColor = __sanitizeColor(out.backgroundColor);
    if (out.color) out.color = __sanitizeColor(out.color);
    if (out['& .MuiTableCell-root']) {
      out['& .MuiTableCell-root'] = {
        ...out['& .MuiTableCell-root'],
        backgroundColor: __sanitizeColor(out['& .MuiTableCell-root'].backgroundColor),
        color: __sanitizeColor(out['& .MuiTableCell-root'].color),
      };
    }
    return out;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {/* Global Search */}
      <Box sx={{ p: 2, pb: 0 }}>
        {enablePagination ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              placeholder="Search..."
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSearchChange && onSearchChange(localSearchQuery);
                }
              }}
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: {
                  color: 'text.primary',
                  '&.Mui-focused': {
                    color: 'text.primary',
                  },
                }
              }}
              sx={{
                flexGrow: 1,
                '& .MuiInput-underline:before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                  borderBottomColor: 'rgba(255, 255, 255, 0.5)',
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: '#6573c3',
                },
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => onSearchChange && onSearchChange(localSearchQuery)}
              sx={{ 
                backgroundColor: '#6573c3',
                '&:hover': { backgroundColor: '#3f51b5' },
                mb: 0.5
              }}
            >
              Search
            </Button>
            {localSearchQuery && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setLocalSearchQuery('');
                  onSearchChange && onSearchChange('');
                }}
                sx={{ 
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  mb: 0.5
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        ) : (
          <TextField
            placeholder="Search across all columns..."
            value={globalFilter || ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
            variant="standard"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            sx: {
              color: 'text.primary',
              '&.Mui-focused': {
                color: 'text.primary',
              },
              '& .MuiInputBase-input': {
                textDecoration: 'none',
              },
              '& input': {
                textDecoration: 'none',
                '&:focus': {
                  textDecoration: 'none',
                },
              },
              '& label': {
                textDecoration: 'none',
                '&.Mui-focused': {
                  textDecoration: 'none',
                },
              },
            }
          }}
          sx={{
            '& .MuiInput-underline:before': {
              borderBottomColor: 'rgba(255, 255, 255, 0.3)',
            },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottomColor: 'rgba(255, 255, 255, 0.5)',
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: '#6573c3',
            },
            '& .MuiInputLabel-root': {
              color: 'text.secondary',
              '&.Mui-focused': {
                color: '#6573c3',
                textDecoration: 'none',
              },
            },
            '& .MuiInputBase-input': {
              textDecoration: 'none',
            }
          }}
        />
        )}
      </Box>

      <TableContainer 
        component={Paper} 
        sx={{ 
          maxHeight: 600,
          width: '100%',
          maxWidth: '100%',
          overflow: 'auto',
          '& .MuiTable-root': {
            minWidth: 'max-content'
          }
        }}
      >
        <Table stickyHeader sx={{ minWidth: 'max-content' }}>
          <TableHead>
            {tableWithEnhancedColumns.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableCell
                    key={header.id}
                    sx={{
                      backgroundColor: '#333333',
                      color: '#ffffff',
                      fontWeight: 'bold',
                      minWidth: 120,
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {tableWithEnhancedColumns.getRowModel().rows.map(row => {
              const rawRowSx = getRowClassName ? getRowClassName(row.original) : undefined;
              const rowSx = __sanitizeRowSx(rawRowSx);
              
              // Function to lighten colors for hover effect
              const getLighterColor = (color) => {
                if (!color || color === 'inherit') return 'rgba(255, 255, 255, 0.1)';
                if (color === '#d32f2f') return '#f44336'; // Lighter red
                if (color === '#f57c00') return '#ff9800'; // Lighter orange
                if (color === '#424242') return '#616161'; // Lighter gray
                return color;
              };
              
              const getHoverTextColor = (bgColor) => {
                if (!bgColor || bgColor === 'inherit') return 'inherit';
                return '#ffffff'; // Keep white text on colored backgrounds
              };
              
              // For tables without custom colors, provide a subtle default hover
              const hasCustomColors = rowSx.backgroundColor && rowSx.backgroundColor !== 'inherit';
              
              return (
                <TableRow
                  key={row.id}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': onRowClick ? { 
                      '& .MuiTableCell-root': {
                        backgroundColor: hasCustomColors 
                          ? getLighterColor(rowSx.backgroundColor)
                          : 'rgba(255, 255, 255, 0.08)',
                        color: hasCustomColors 
                          ? getHoverTextColor(rowSx.backgroundColor)
                          : 'inherit',
                        transition: 'background-color 0.2s ease, color 0.2s ease',
                      }
                    } : {},
                    ...rowSx,
                  }}
                >
                  {row.getVisibleCells().map(cell => (
          <TableCell 
                      key={cell.id}
                      sx={{
                        minWidth: 120,
                        whiteSpace: 'nowrap',
                        padding: '8px 12px',
                        backgroundColor: rowSx.backgroundColor || 'inherit',
                        color: rowSx.color || 'inherit',
                        transition: 'background-color 0.2s ease, color 0.2s ease',
            ...(rowSx['& .MuiTableCell-root'] || {}),
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination Controls or Table Info */}
      {enablePagination && pagination ? (
        <PaginationControls 
          pagination={pagination}
          onPaginationChange={onPaginationChange}
        />
      ) : (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {tableWithEnhancedColumns.getRowModel().rows.length} of {data.length} rows
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TanStackTable;